import { Component, type ReactNode } from "react";
import { Button, Center, Stack, Text, Title } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Center h="100dvh">
          <Stack align="center" gap="lg" maw={400}>
            <IconAlertTriangle size={48} color="var(--mantine-color-red-6)" />
            <Title order={3}>Something went wrong</Title>
            <Text size="sm" c="dimmed" ta="center">
              {this.state.error?.message ?? "An unexpected error occurred."}
            </Text>
            <Button variant="light" onClick={this.handleReset}>
              Try again
            </Button>
          </Stack>
        </Center>
      );
    }

    return this.props.children;
  }
}
